import moment from 'moment';
import showNotification from '../components/extras/showNotification';
import i18next, {t} from 'i18next';
import isEmpty from 'lodash/isEmpty'
import {demoPages} from "../menu";
import Configs from "../models/Configs";
import {checkIsRestPlanModify} from "../pages/AuthMenu/Approval/helpers";
import React from "react";
import Badge from "../components/bootstrap/Badge";
import { getLocalizedField, localizedLabel, normalizeExcelHeaders, WORK_TYPE_NAME_OPTIONS } from 'helpers/localizedField';
import { getPersonDisplayName } from 'helpers/personNameI18n';
import { formatDateRange } from 'helpers/helpers';

/** 직원등록·직원초대 엑셀 헤더(한국어 원본 키). 업로드 데이터는 이 키로 서버에 보낸다 */
const MEMBER_EXCEL_KEYS = ['이름', '휴대폰번호', '부서', '근무그룹', '직위', '입사일', '사번', '이메일'];

/** 기사용 휴가 업로드 엑셀 헤더 [한국어 원본 키, 번역 키]. 업로드 데이터는 원본 키로 서버에 보낸다 */
const REST_EXCEL_COLS = [
	['이름', 'common.downloadExcelAn.rest.name'],
	['사번', '사번'],
	['사용 시작일자', 'common.downloadExcelAn.rest.usedStartDate'],
	['사용 종료일자', 'common.downloadExcelAn.rest.usedEndDate'],
];

/**
 * 기사용 휴가 업로드 샘플 다운로드. 서버 샘플(/userCompany/sampleDownload?type=restxls)과 같은 양식을 현재 언어로 만든다
 * @param {Function} t
 */
export function restSampleDownload(t = i18next.t) {
	const xlsx = require('sheetjs-style-v2');
	const today = moment().format('YYYY-MM-DD');
	const ws = xlsx.utils.aoa_to_sheet([
		REST_EXCEL_COLS.map(([, labelKey]) => t(labelKey)),
		[t('restManage.restSampleName'), '20140123', today, today],
	]);

	// 서버 샘플과 같은 서식
	const border = { left: { style: 'thin' }, right: { style: 'thin' }, top: { style: 'thin' }, bottom: { style: 'thin' } };
	REST_EXCEL_COLS.forEach((_, c) => {
		ws[xlsx.utils.encode_cell({ r: 0, c })].s = {
			font: { name: 'Calibri', sz: 12, bold: true, color: { rgb: 'FFFFFFFF' } },
			fill: { patternType: 'solid', fgColor: { rgb: 'FF556EE6' } },
			border,
			alignment: { horizontal: 'center', vertical: 'center' },
		};
		ws[xlsx.utils.encode_cell({ r: 1, c })].s = {
			fill: { patternType: 'solid', fgColor: { rgb: 'FFFFFFFF' } },
			border,
			alignment: { horizontal: 'center' },
		};
	});
	ws['!cols'] = REST_EXCEL_COLS.map(() => ({ wch: 24 }));

	const wb = xlsx.utils.book_new();
	xlsx.utils.book_append_sheet(wb, ws, t('restManage.restSampleSheet'));
	xlsx.writeFile(wb, `${t('restManage.restSampleFile')}.xlsx`);
}

/******************************************************************
 * 파일명 : frontend2\src\lib\Util.js
 * 설명 : frontend에서 사용하는 유틸리티
 ******************************************************************/

function getDateTimeField(date) {
	return (date && moment(date).isValid()) ? new Date(date).toLocaleString("ko-KR", { timeZone: "Asia/Seoul" }) : (date || '');
}

export function remainingRest(restDispUnit, restHour, standardHur, type, returnValue) {
	if(restHour === 0 || !restHour) return returnValue ?? '-';

	let totalHrs = parseFloat(restHour || 0),
	    day = totalHrs / standardHur,
	    hrs = totalHrs % standardHur;

	if (type === 'day') {
		return `${day}${t('common.label.sun')}`;
	}else if(type === 'hour'){
		return `${hrs}${t('common.label.hours')}`;
	}else if(type === 'totalHour'){
		return `${totalHrs}${t('common.label.hours')}`;
	}

	const dayFloor = Math.sign(day) * Math.floor(Math.abs(day));

	const formatDayDisp = () => {
		hrs = Math.round(hrs);
		return `${dayFloor !== 0 ? `${dayFloor}${t('common.label.sun')}` : ''}
		 ${hrs > 0 ? `${hrs}${t('common.label.hours')}` : (hrs < 0 && day === 0 ? `${hrs}${t('common.label.hours')}` : '')}`.trim();
	}

	switch(restDispUnit){
		case 'HALF':
			// 0.5 단위는 반차로, 그 외는 일 단위로 표기
			return `${hrs && Math.abs(hrs) !== standardHur / 2 
				? formatDayDisp() 
				: `${dayFloor + (hrs ? 0.5 * Math.sign(hrs) : 0)}${t('common.label.sun')}`
			}`;
		case 'TIME':
			return `${totalHrs}${t('common.label.hours')}`
		case 'DAY':
			return formatDayDisp();
		case 'ALL':
			return `${formatDayDisp()}(${totalHrs}${t('common.label.hours')})`
		default:
			return '';
	}
}

export function remainingRestDetail(restHour, standardHur, type, restDispUnit, returnValue) {
	if(restHour === 0 || !restHour) return returnValue ?? '-';
	let totalHrs = parseFloat(restHour || 0),
		day = totalHrs / standardHur,
		hrs = totalHrs % standardHur;

	const dayFloor = Math.sign(day) * Math.floor(Math.abs(day));

	const formatDayDisp = () =>
		`${dayFloor !== 0 ? `${dayFloor}D` : ''}
		 ${hrs > 0 ? `${hrs}H` : (hrs < 0 && day === 0 ? `${hrs}H` : '')}`.trim();

	switch(restDispUnit){
		case 'HALF':
			// 0.5 단위는 반차로, 그 외는 일 단위로 표기
			return `${hrs && Math.abs(hrs) !== standardHur / 2 
				? formatDayDisp()
				: dayFloor + (hrs ? 0.5 * Math.sign(hrs) : 0)
			}`;
		case 'TIME':
			return `${totalHrs}`;
		case 'DAY':
			return formatDayDisp();
		case 'ALL':
			if(type === 'TIME'){
				return `${totalHrs}H`;
			}else{
				return formatDayDisp();
			}
		default:
			return '';
	}
};

export function getTitle(request, t, isSimple = false) { // isSimple 사용시 문서 명 뒤에 신청 제외
	// 휴가·근무 유형 이름은 API 번역값(nameEng 등)을 먼저 쓰고, 없으면 기존처럼 t(한글 이름)
	const dataTitle = request?.data?.title;
	const translatedTitle = dataTitle
		? t(dataTitle) || dataTitle
		: request?.restday?.name
			? localizedLabel(t, request.restday)
			: localizedLabel(t, request?.workType, 'abntdWorkTypNm', WORK_TYPE_NAME_OPTIONS);

	switch (request?.type) {
		case 'RECORD': // 근무기록 수정 + 관리자 수정 + 엑셀 일괄 업로드
			return request.requestMemo || t('approval.modifyRecord');

		case 'REST_TRANSFER': // 이월연차 이관 신청
			return t('연차 이월 동의완료');

		case 'USER_REST': // 휴가
			return request?.planGroupId
				? t(checkIsRestPlanModify(request))
				: isSimple
					? translatedTitle
					: `${translatedTitle} ${t('approval.request')}`;

		case 'WORK':
		case 'REDUCE': // 단축근무 + 근무
			return  isSimple
				? translatedTitle
				: `${translatedTitle} ${t('approval.request')}`;

		// 취소 신청 리스트
		case 'WORK_CANCEL':
		case 'USER_REST_CANCEL':
		case 'REST_TRANSFER_CANCEL':
		case 'RECORD_CANCEL':
		case 'REDUCE_CANCEL':
			return isSimple
				? `${translatedTitle} ${t('approval.cancelStatus')}`
				: `${translatedTitle} ${t('approval.requestCancel')}`;

		case 'REST_PLAN': // 연차 계획
			return t(checkIsRestPlanModify(request));

		case 'PREGNANCY': // 임산부 대상자 신청
		case 'PREGNANCY_CANCEL': // 임산부 대상자 취소
			return `${t('userPregnancy.pregWomenTargetApp')} ${request?.type?.includes('CANCEL')? t('Cancle') : ''}`;

		default:
			return '-';
	}
}

/**
 * 연차계획 신청/수정 총 사용시간 (종일 1일, 반차 0.5일, 시간단위는 사용시간 합산)
 * 결재현황 카드 / 전자결재 메뉴 / 결재 상세 팝업 공통
 */
export function getRestPlanTotalHours(request, standardHur = request?.standardHur || 8) {
    const hoursOf = (item) =>
        item.type === 'full' ? standardHur
        : item.type === 'direct' ? (item.usedType === 'annual' ? item.annualUsed : item.monthlyUsed) || 0
        : item.unit === 'half' ? standardHur / 2
        : 0;
    return (request?.data?.list || []).reduce((total, item) => total + hoursOf(item), 0);
}

/**
 * 결재 문서 내용 컬럼 문자열 반환 (결재현황 카드 / 전자결재 메뉴 공통)
 * @param {object} request  결재 데이터
 * @param {Function} t      useTranslation().t
 * @param {string} restDispUnit  'DAY' | 'HOUR'
 * @param {{ dateFmt?: string, multiDayFmt?: string, asString?: boolean, showHistory?: boolean }} options
 *   dateFmt: 단일날짜 포맷, multiDayFmt: 범위(여러날) 포맷(기본=dateFmt), asString: RECORD 문자열 반환, showHistory: false 시 RECORD 히스토리 생략
 */
export function getApprovalContent(request, t, restDispUnit, { dateFmt = 'YYYY-MM-DD', multiDayFmt, asString = false, showHistory = true } = {}) {
    const rangeFmt = multiDayFmt || dateFmt;
    if (!request?.startDate) return '-';

    const startM = moment(request.startDate);
    const endM   = moment(request.endDate || request.startDate);
    const startDate = startM.format(dateFmt);
    const endDate   = endM.format(dateFmt);
    const isSameDay = startM.isSame(endM, 'day');

    const isCancel = String(request?.type).endsWith('_CANCEL');
    const sign = isCancel ? '-' : '';
    const baseType = isCancel ? request.type.replace('_CANCEL', '') : request.type;

    switch (baseType) {
        case 'RECORD': {
            if (!showHistory) return `${startDate} (${startM.format('ddd')})`;
            const hist = request?.cc?.dates?.history;
            const AttendStart = hist?.find(e => e.state === 'S' && e.restday.name === '근무') || null;
            const AttendEnd = hist ? [...hist].reverse().find(e => e.state === 'E' && e.restday.name === '근무') || null : null;
            const isNextDay = AttendEnd && !moment(AttendEnd.realTime).isSame(endM, 'day');
            const unspecific = t('approval.unspecific');
            const fmtAttend = (a) => a ? `${moment(a.realTime).format('YYYY-MM-DD')} | ${moment(a.realTime).format('HH:mm')}` : unspecific;
            if (asString) {
                return `${fmtAttend(AttendStart)} > ${request?.startTime || ''} || ${fmtAttend(AttendEnd)} > ${request?.endTime || ''}${isNextDay ? ' (+1일)' : ''}`;
            }
            return (
                <>
                    <div>{fmtAttend(AttendStart)} &gt; {request?.startTime || ''}</div>
                    <div>{fmtAttend(AttendEnd)} &gt; {request?.endTime || ''}{isNextDay && ' (+1일)'}</div>
                </>
            );
        }

        case 'REST':
        case 'USER_REST': {
            if (request?.planGroupId) {
                // 연차계획: 결재 상세 팝업과 동일 기준으로 일수 표기
                const unit = !restDispUnit || restDispUnit === 'ALL' ? 'DAY' : restDispUnit;
                const days = remainingRest(unit, getRestPlanTotalHours(request), request?.standardHur || 8, '', '');
                const dayStr = days ? ` · ${days}` : '';
                if (isSameDay) return `${startDate}${dayStr}`;
                return `${startM.format(rangeFmt)} ~ ${endM.format(rangeFmt)}${dayStr}`;
            }
            if (request?.requestMemo === '이월 연차') return `${request?.deduction || 0}${t('common.label.sun')}`;
            const amount = remainingRest(restDispUnit, request?.deduction, request?.standardHur);
            const amountStr = amount && amount !== '-' ? ` · ${sign}${amount}` : '';
            if (isSameDay) return `${startDate} (${startM.format('ddd')})${amountStr}`;
            return `${startM.format(rangeFmt)} ~ ${endM.format(rangeFmt)}${amountStr}`;
        }

        case 'WORK':
        case 'PREGNANCY':
        case 'REDUCE': {
            const isNightWork = moment(request?.endTime, 'HH:mm').isBefore(moment(request?.startTime, 'HH:mm'));
            if ((isSameDay || request?.workType?.abntdChcPrdTypCd === 'ONEDAY' || isNightWork) && request?.startTime && request?.endTime) {
                const raw = formatDateRange(
                    startM.format('YYYY-MM-DD'),
                    request.startTime,
                    endM.format('YYYY-MM-DD'),
                    request.endTime,
                    t,
                    request?.data?.row?.total || request?.data?.ot,
                    isNightWork,
                );
                if (typeof raw !== 'string') return raw;
                return raw
                    .replace(/(\d{4})-(\d{2})-(\d{2})/g, (_, y, m, d) =>
                        dateFmt === 'YY.MM.DD' ? `${y.slice(2)}.${m}.${d}` : `${y}-${m}-${d}`
                    )
                    .replace(/\[/g, `· ${sign}`)
                    .replace(/\]/g, '')
                    .trimEnd();
            }
            if (isSameDay) return `${startDate} (${startM.format('ddd')})`;
            const diffDays = request?.data?.list?.length ?? endM.diff(startM, 'd') + 1;
            return `${startM.format(rangeFmt)} ~ ${endM.format(rangeFmt)} · ${sign}${diffDays}${t('일')}`;
        }

        case 'RECORD_CANCEL':
            return `${startM.format(rangeFmt)} ~ ${endM.format(rangeFmt)}`;

        default: {
            if (isSameDay) return `${startDate} (${startM.format('ddd')})`;
            const diffDays = endM.diff(startM, 'd') + 1;
            return `${startM.format(rangeFmt)} ~ ${endM.format(rangeFmt)} · ${sign}${diffDays}${t('일')}`;
        }
    }
}

export function getStateText(state, t) {
	return state === 'SUCCESS' ? (
		<Badge
			style={{
				borderRadius: 4,
				backgroundColor: '#ECF8E3',
				color: '#29BC86',
				textWrap: 'wrap',
				fontSize: 12,
			}}
			color='#ECF8E3'
		>
			{t('재직')}
		</Badge>
	) : state === 'LEAVE' ? (
		<Badge
			style={{
				borderRadius: 4,
				backgroundColor: '#FFECEC',
				color: '#F17474',
				textWrap: 'wrap',
				fontSize: 12,
			}}
			color='#FFECEC'
		>
			{t('퇴직')}
		</Badge>
	) : state === 'REQUEST' ? (
		<Badge
			style={{
				borderRadius: 4,
				backgroundColor: '#EFEFEF',
				color: '#F17474',
				textWrap: 'wrap',
				fontSize: 12,
			}}
			color='#EFEFEF'
		>
			{t('요청')}
		</Badge>
	) : state === 'REST' ? (
		<Badge
			style={{ borderRadius: 4,
				backgroundColor: '#F2EFFF',
				color: '#7156EE',
				textWrap: 'wrap',
				fontSize: 12,
			}}
			color='#F2EFFF'
		>
			{t('filters.leaveOfAbsence')}
		</Badge>
	) : state === 'SUSPENSION' ? (
		<Badge
			style={{
				borderRadius: 4,
				backgroundColor: '#F2EFFF',
				color: '#7156EE',
				textWrap: 'wrap',
				fontSize: 12,
			}}
			color='#F2EFFF'
		>
			{t('정직(징계)')}
		</Badge>
	) : state === 'DENY' ? (
		<Badge
			style={{
				borderRadius: 4,
				backgroundColor: '#F2EFFF',
				color: '#7156EE',
				textWrap: 'wrap',
				fontSize: 12,
			}}
			color='#F2EFFF'
		>
			{t('restManage.deny')}
		</Badge>
	) : (
		<Badge
			style={{
				borderRadius: 4,
				backgroundColor: '#F2EFFF',
				color: '#7156EE',
				textWrap: 'wrap',
				fontSize: 12,
			}}
			color='#F2EFFF'
		>
			{state === t('restManage.yearBeforeJoin')
				? t('restManage.beforeYearEmployment')
				: t('restManage.cannotConfirm')
			}
		</Badge>
	);
};

export function getBadgeInfo(approval, t) {
	if (approval?.type === 'WORK') {
		if (approval?.workType?.abntdWorkTypCd === 1) {
			return {
				bg: '#FF8B8F',
				fc: '#fff',
				name: t('휴일')
			};
		} else if (approval?.workType?.abntdWorkTypCd === 2) {
			return {
				bg: '#FF8B8F',
				fc: '#fff',
				name: t('approval.overtime')
			};
		} else if (approval?.workType?.abntdSchdlRfltYn) {
			return {
				bg: '#FFC107',
				fc: '#fff',
				name: t('approval.presumedWork')
			};
		} else if (approval?.workType?.abntdAgwrkExpsrYn) {
			return { bg: '#FDF9EA', fc: '#661A02', name: t('approval.flexible') };
		}
		return { bg: '#649FF8', fc: '#fff', name: t('approval.working') };
	}

	if (['REST', 'USER_REST'].includes(approval?.type)) {
		return {
			bg: '#47BC86',
			fc: '#fff',
			name: approval?.planGroupId ? checkIsRestPlanModify(approval) : t('approval.vacation')
		};
	}

	if (approval?.type === 'RECORD' || approval?.type?.includes('CANCEL')) {
		return {
			bg: '#9B86F5',
			fc: '#fff',
			name: approval?.type === 'RECORD' ? t('수정') : t('common.cancelRequest')
		};
	}

	if (approval?.type === 'REST_PLAN') {
		return {
			bg: '#649FF8',
			fc: '#fff',
			name: checkIsRestPlanModify(approval)
		};
	}

	if (approval?.type === 'PREGNANCY') {
		return {
			bg: '#ffe6dd',
			fc: '#fc622f',
			name: t('userPregnancy.maternalProtection')
		};
	}

	if (approval?.type === 'REDUCE') {
		return {
			bg: '#FFF3E8',
			fc: '#E6781E',
			name: localizedLabel(t, approval?.restday)
		};
	}

	return {
		bg: '#649FF8',
		fc: '#fff',
		name: localizedLabel(t, approval?.restday)
	};
}

export function getTextColorByBackgroundColor(hexColor) {
	const c = hexColor.substring(1); // 색상 앞의 # 제거
	const rgb = parseInt(c, 16); // rrggbb를 10진수로 변환
	const r = (rgb >> 16) & 0xff; // red 추출
	const g = (rgb >> 8) & 0xff; // green 추출
	const b = (rgb >> 0) & 0xff; // blue 추출

	const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b; // per ITU-R BT.709

	// 색상 선택
	return luma < 127.5 ? 'white' : 'dark'; // 글자색이
}

export function divisionArray(arr, n) {
	const len = arr.length;
	const cnt = Math.floor(len / n) + (Math.floor(len % n) > 0 ? 1 : 0);
	let tmp = [];
	for (var i = 0; i < cnt; i++) {
		tmp.push(arr.splice(0, n));
	}
	return tmp;
}

export function moveLoginPage()  {
	const basicLoginUrl = `/${demoPages.login.path}`;
	const metanetIps = process.env.REACT_APP_METANET_IP?.split(',') || [];

	// 메타타임 로그인페이지가 아닐때만 실행(무한루프 방지)
	if (window.location.pathname !== basicLoginUrl) {
		// 메타넷에서 접속 시 기본 로그인페이지로 이동
		if (metanetIps.includes(Configs.publicIp?.trim())) {
			window.location.href = basicLoginUrl;
		}
		// 그 외 장소에서 접속시에는 지정해둔 로그인 페이지로 이동
		else{
			window.location.href = (process.env.REACT_APP_LOGIN_URL) ? process.env.REACT_APP_LOGIN_URL : basicLoginUrl;
		}
	}
}

export function convertToYYYYMMDD(dateString) {
	const formattedDate = moment(dateString, ['YYYY-MM-DD', 'YYYYMMDD', 'YYYY_MM_DD', 'YYYY/MM/DD', 'MM/DD/YYYY', 'MM-DD-YYYY', 'MM_DD_YYYY', 'YYYY.MM.DD']).format('YYYY-MM-DD');
	return formattedDate;
}

export const checkMoment = (input, format = 'YYYY-MM-DD') => {
	const formats = ['YYYY-MM-DD', 'YYYYMMDD', 'YYYY_MM_DD', 'YYYY/MM/DD', 'MM/DD/YYYY', 'MM-DD-YYYY', 'MM_DD_YYYY', 'YYYY.MM.DD']; // 이후 계속 추가
	for (let i = 0; i < formats.length; i++) {
		if (moment(input, formats[i]).isValid() === true) return moment(input, formats[i]).format(format);
	}
	return input;
};

export function s2ab(s) {
	var buf = new ArrayBuffer(s.length); //convert s to arrayBuffer
	var view = new Uint8Array(buf); //create uint8array as viewer
	for (var i = 0; i < s.length; i++) view[i] = s.charCodeAt(i) & 0xff; //convert to octet
	return buf;
}

export async function verificationFile(file, validTableData, userList, companyId, type) {
	if (file) {
		return new Promise(async (resolve) => {
			const xlsx = require('xlsx');
			const reader = new FileReader();
			let excelKeys = [];
			switch (type) {
				case 'rest':
					// 사용자 검증 / 사용 시작일자 / 사용 종료일자 / 휴가여부
					reader.onload = (e) => {
						//let userList = userCompanyList.map((x) => x.user);
						let result = true;
						let target = e.target.result;
						const wb = xlsx.read(target, { type: 'binary' });
						const sheetname = wb.SheetNames[0];
						// 번역된 양식도 받도록 헤더를 한국어 키로 맞춘다. 서버에 헤더 행을 그대로 보내므로 한국어 키여야 한다
						let data = validTableData?.length > 0 ? validTableData : normalizeExcelHeaders(xlsx.utils.sheet_to_json(wb.Sheets[sheetname], { raw: false }), REST_EXCEL_COLS);
						/*const newdata = validTableData?.length > 0 ? validTableData : translateArrayOfObjectsToKorean(data);
						data = getItem('i18nextLng') === 'ko' ? data : newdata;*/
						const regExpEmail = /^[0-9a-zA-Z]([-_\.]?[0-9a-zA-Z])*@[0-9a-zA-Z]([-_\.]?[0-9a-zA-Z])*\.[a-zA-Z]{2,3}$/i;

						for (let i = 0; i < data.length; i++) {
							//사용 시작일자 유효성 체크
							if (data[i]['사용 시작일자'].indexOf('-') < 0) {
								data[i]['사용 시작일자'] = data[i]['사용 시작일자']
									.split('/')
									.map((x) => (x.length == 1 ? '0' + x : x))
									.join('/');
 
								if (!moment(data[i]['사용 시작일자'], 'MM/DD/YY', true).isValid()) {
									data[i]['사용 시작일자'] = validTableData?.length > 0 ? data[i]['사용 시작일자'] : moment(data[i]['사용 시작일자'], 'MM/DD/YY').format('YYYY-MM-DD');
									data[i]['사용 시작일자'] = data[i]['사용 시작일자'].indexOf('#') > -1 ? data[i]['사용 시작일자'] : '#' + data[i]['사용 시작일자'] + '#';
									result = false;
								} else {
									data[i]['사용 시작일자'] = moment(data[i]['사용 시작일자'], 'MM/DD/YY').format('YYYY-MM-DD');
								}
							} else {
 
								if (!moment(data[i]['사용 시작일자'], 'YYYY-MM-DD', true).isValid()) {
									data[i]['사용 시작일자'] = validTableData?.length > 0 ? data[i]['사용 시작일자'] : moment(data[i]['사용 시작일자'], 'YYYY-MM-DD').format('YYYY-MM-DD');
									data[i]['사용 시작일자'] = data[i]['사용 시작일자'].indexOf('#') > -1 ? data[i]['사용 시작일자'] : '#' + data[i]['사용 시작일자'] + '#';
									result = false;
								}
							}
							//사용 종료일자 유효성 체크
							if (data[i]['사용 종료일자'].indexOf('-') < 0) {
								data[i]['사용 종료일자'] = data[i]['사용 종료일자']
									.split('/')
									.map((x) => (x.length == 1 ? '0' + x : x))
									.join('/');
 
								if (!moment(data[i]['사용 종료일자'], 'MM/DD/YY', true).isValid()) {
									data[i]['사용 종료일자'] = validTableData?.length > 0 ? data[i]['사용 종료일자'] : moment(data[i]['사용 종료일자'], 'MM/DD/YY').format('YYYY-MM-DD');
									data[i]['사용 종료일자'] = data[i]['사용 종료일자'].indexOf('#') > -1 ? data[i]['사용 종료일자'] : '#' + data[i]['사용 종료일자'] + '#';
									result = false;
								} else {
									data[i]['사용 종료일자'] = moment(data[i]['사용 종료일자'], 'MM/DD/YY').format('YYYY-MM-DD');
								}
							} else {
								if (!moment(data[i]['사용 종료일자'], 'YYYY-MM-DD', true).isValid()) {
									data[i]['사용 종료일자'] = validTableData?.length > 0 ? data[i]['사용 종료일자'] : moment(data[i]['사용 종료일자'], 'YYYY-MM-DD').format('YYYY-MM-DD');
									data[i]['사용 종료일자'] = data[i]['사용 종료일자'].indexOf('#') > -1 ? data[i]['사용 종료일자'] : '#' + data[i]['사용 종료일자'] + '#';
									result = false;
								}
							}
							/*
							const checkValid = (str) => {
								str = str.replace(/ /g, ''); // 공백 제거
								return str.charAt(0) == '#' && str.charAt(str.length - 1) == '#' ? str.substring(1, str.length - 1) : str; // '#'있다면 제거
							};
							// 이메일 유효성체크 준비
							data[i]['이메일'] = checkValid(data[i]['이메일']);

							//이름과 이메일 매칭 체크
							let nameUser = userList.find((x) => x.name == data[i]['이름'])?.email;
							let emailUser = userList.find((x) => x.email == data[i]['이메일'])?.email;

							//이메일 형식 체크
							if (data[i]['이메일'].length < 6 || !regExpEmail.test(data[i]['이메일']) || !userList.find((x) => x.email == data[i]['이메일']) || nameUser !== emailUser) {
								data[i]['이메일'] = data[i]['이메일'].indexOf('#') > -1 ? data[i]['이메일'] : '#' + data[i]['이메일'] + '#';
								result = false;
							}*/

							// 사번 유효성 체크
							if (!userList.some(user => user.companyNumber === data[i]['사번'].replace(/ /g, ''))) {
								data[i]['사번'] = data[i]['사번'].indexOf('#') > -1 ? data[i]['사번'] : '#' + data[i]['사번'] + '#';
								result = false;
							}

							//사용자 유효성 체크
							if (!userList.some(user => user.nickName === data[i]['이름'].replace(/ /g, ''))) {
								data[i]['이름'] = data[i]['이름'].indexOf('#') > -1 ? data[i]['이름'] : '#' + data[i]['이름'] + '#';

								// 기존 소스
								// data[i]['이메일'] = data[i]['이메일'].indexOf('#') > -1 ?
								// data[i]['이메일'] :  '#' + data[i]['이메일'] + '#'
								result = false;
							}
						}
						resolve({ data: data, result: result });
					};
					reader.readAsBinaryString(file);
					break;

				case 'work':
					excelKeys = ['name', 'companyNumber', 'date', 'startTime', 'endTime', 'ccCode1', 'ccCode2', 'ccCode3'];
					// 근태 업로드
					reader.onload = (e) => {
						let result = true;
						let target = e.target.result;
						const wb = xlsx.read(target, { type: 'binary' });
						const sheetname = wb.SheetNames[0];
						let data = validTableData?.length > 0 ? validTableData : xlsx.utils.sheet_to_json(wb.Sheets[sheetname], { raw: false, defval: ' ' }); // 데이터가 없는 셀의 기본값 공백(' ')으로 설정

 

						const constructionSite = t ? t('workingLog.constructionSite') : '공사현장';

						const keycc1 = t ? t('workingLog.attendanceCc1') : '근태CC 1';
						const keycc2 = t ? t('workingLog.attendanceCc2') : '근태CC 2';
						const keycc3 = t ? t('workingLog.attendanceCc3') : '근태CC 3';

						for (let i = 0; i < data.length; i++) {
							if (Object.keys(data[i]).includes(constructionSite)) {
								result = false;
								return resolve({ msg: 'notMatchingType' });
							}
							// 컬럼별 validation
							const keyDate = t ? t('일자') : '일자';
							if (!moment(convertToYYYYMMDD(data[i][keyDate]), 'YYYY-MM-DD', true).isValid()) {
								data[i][keyDate] = validTableData?.length > 0 ? data[i][keyDate] : moment(convertToYYYYMMDD(data[i][keyDate]), 'YYYY-MM-DD').format('YYYY-MM-DD');
								data[i][keyDate] = data[i][keyDate].indexOf('#') > -1 ? data[i][keyDate] : '#' + data[i][keyDate] + '#';
								result = false;
							} else {
								data[i][keyDate] = convertToYYYYMMDD(data[i][keyDate]);
							}
							
 
							const startTime = t ? t('workingLog.workStartTime') : '근무 시작시간';
							

							// 231228 근무 시작 시간이 HH:mm, H:mm 두 형식에 맞게 들어오지 않을때, #을 붙여서 업로드 방지하도록 유효성 체크 추가
							if (!moment(data[i][startTime], 'HH:mm', true).isValid() && !moment(data[i][startTime], 'H:mm', true).isValid()) {
								data[i][startTime] = validTableData?.length > 0 ? data[i][startTime] : moment(data[i][startTime], 'H:mm').format('HH:mm');
								// H:mm을 HH:mm으로 형식을 바꿨는데도 불구하고, Valid 오류가 발생하게 된다면, 그때 result를 false로 처리
								// 여기서 처리를 하더라도, backend 로직에서 시간 type이 맞지 않는다면 attend save시 exception이 발생함.
								if (!moment(data[i][startTime], 'HH:mm', true).isValid() && !moment(data[i][startTime], 'H:mm', true).isValid()) {
									data[i][startTime] = data[i][startTime].indexOf('#') > -1 ? data[i][startTime] : '#' + data[i][startTime] + '#';
									result = false;
								}
							}
							const endTime = t ? t('workingLog.workEndTime') : '근무 종료시간';
							// 근무 시작 시간과 동일하게 변경
							if (!moment(data[i][endTime], 'HH:mm', true).isValid() && !moment(data[i][endTime], 'H:mm', true).isValid()) {
								data[i][endTime] = validTableData?.length > 0 ? data[i][endTime] : moment(data[i][endTime], 'H:mm').format('HH:mm');
								if (!moment(data[i][endTime], 'HH:mm', true).isValid() && !moment(data[i][endTime], 'H:mm', true).isValid()) {
									data[i][endTime] = data[i][endTime].indexOf('#') > -1 ? data[i][endTime] : '#' + data[i][endTime] + '#';
									result = false;
								}
							}

							

							// ❗ 필요한 key가 하나도 없으면 이 row는 skip
							if (!(keycc1 in data[i] || keycc2 in data[i] || keycc3 in data[i])) {
								excelKeys = ['name', 'companyNumber', 'date', 'startTime', 'endTime'];
								continue;
							}
							// 근태CC 1,2,3 validation -> 세 컬럼 중 한 컬럼의 값만 존재해야 함
							let count = 0;
							for (let key in data[i]) {
																
								console.log('key',key)
								if ([keycc1, keycc2, keycc3].includes(key) && !!data[i][key]?.trim()) {
									count++;
									if (count > 1) {
										data[i][keycc1] = data[i][keycc1].indexOf('#') > -1 ? data[i][keycc1] : '#' + data[i][keycc1] + '#';
										data[i][keycc2] = data[i][keycc2].indexOf('#') > -1 ? data[i][keycc2] : '#' + data[i][keycc2] + '#';
										data[i][keycc3] = data[i][keycc3].indexOf('#') > -1 ? data[i][keycc3] : '#' + data[i][keycc3] + '#';
										result = false;
										continue;
									}
								}
							}

						}
						if (validTableData?.length > 0 && result == true) {
							for (let i = 0; i < data.length; i++) {
								let updatedKeyData = {};
								excelKeys.forEach((key, index) => {
									updatedKeyData[key] = data[i][Object.keys(data[i])[index]].trim();
								});
								updatedKeyData['rowNum'] = i.toString();
								if ((keycc1 in data[i] && keycc2 in data[i] && keycc3 in data[i])) {
									updatedKeyData['ccCode'] = !!updatedKeyData['ccCode1'] ? updatedKeyData['ccCode1'] : !!updatedKeyData['ccCode2'] ? updatedKeyData['ccCode2'] : updatedKeyData['ccCode3'];
								}
								data[i] = updatedKeyData;
							}
						} 

						resolve({ data: data, result: result });
					};
					reader.readAsBinaryString(file);
					break;
				case 'constr':
					excelKeys = ['name', 'companyNumber', 'date', 'constrSite', 'constrRate', 'constrRank', 'constrType', 'constrYear', 'constrGrade', 'constrMgr'];
					// 공사수당 업로드
					reader.onload = (e) => {
						let result = true;
						let target = e.target.result;
						const wb = xlsx.read(target, { type: 'binary' });
						const sheetname = wb.SheetNames[0];
						let data = validTableData?.length > 0 ? validTableData : xlsx.utils.sheet_to_json(wb.Sheets[sheetname], { raw: false, defval: ' ' }); // 데이터가 없는 셀의 기본값 공백(' ')으로 설정

						for (let i = 0; i < data.length; i++) {
							const startTime = t ? t('workingLog.workStartTime') : '근무 시작시간';
							if (Object.keys(data[i]).includes(startTime)) {
								result = false;
								return resolve({ msg: 'notMatchingType' });
							}
							// 컬럼별 validation
							const keyDate = t ? t('일자') : '일자'; 
							if (!moment(data[i][keyDate], 'YYYY-MM-DD', true).isValid()) {
								data[i][keyDate] = validTableData?.length > 0 ? data[i][keyDate] : moment(data[i][keyDate], 'YYYY-MM-DD').format('YYYY-MM-DD');
								data[i][keyDate] = data[i][keyDate].indexOf('#') > -1 ? data[i][keyDate] : '#' + data[i][keyDate] + '#';
								result = false;
							}
						}
						if (validTableData?.length > 0 && result == true) {
							for (let i = 0; i < data.length; i++) {
								let updatedKeyData = {};
								excelKeys.forEach((key, index) => {
									updatedKeyData[key] = data[i][Object.keys(data[i])[index]]?.trim();
								});
								updatedKeyData['rowNum'] = i.toString();
								data[i] = updatedKeyData;
							}
						}
						resolve({ data: data, result: result });
					};
					reader.readAsBinaryString(file);
					break;
				default:
					// 직원등록,직원초대 유효성 체크
					//휴대폰번호 / 입사일 / 이메일
 
					reader.onload = (e) => {
						let target = e.target.result;
						const wb = xlsx.read(target, { type: 'binary' });
						const sheetname = wb.SheetNames[0];
						// 번역된 양식(헤더가 영어 등)도 받도록 헤더를 한국어 키로 맞춘다. 서버에도 한국어 키로 보낸다
						let data = validTableData?.length > 0 ? validTableData : normalizeExcelHeaders(xlsx.utils.sheet_to_json(wb.Sheets[sheetname], { raw: false }), MEMBER_EXCEL_KEYS);

						// const patternPhone = new RegExp("01[016789]-[0-9]{2,3}-[0-9]{3,4}");
						//const patternPhone = new RegExp("01[016789]-[^0][0-9]{2,3}-[0-9]{3,4}");
						const patternPhone = /01[016789]-[0-9]{3,4}-[0-9]{4}/;
						const regExpEmail = /^[0-9a-zA-Z]([-_\.]?[0-9a-zA-Z])*@[0-9a-zA-Z]([-_\.]?[0-9a-zA-Z])*\.[a-zA-Z]{2,3}$/i;
						let emails = [];
						let result = true;

						for (let i = 0; i < data.length; i++) {
							let verificationData = {};
							//휴대폰 번호 형식
							if (!patternPhone.test(data[i]['휴대폰번호'])) {
								data[i]['휴대폰번호'] = data[i]['휴대폰번호'].indexOf('#') > -1 ? data[i]['휴대폰번호'] : '#' + data[i]['휴대폰번호'] + '#';
								result = false;
							}
							//입사일 유효성 체크
							//MM/DD/YY

							// 이메일 유효성체크 준비
							let email = data[i]['이메일'].replace(/ /g, ''); // 공백 제거
							//이메일 중복 체크

							if (emails.find((x) => x === email)) {
								data[i]['이메일'] = '중복';
								result = false;
							} else {
								emails.push(email);
							}
							//이메일 형식 체크
							if (email.length < 6 || !regExpEmail.test(email)) {
								data[i]['이메일'] = data[i]['이메일'].indexOf('#') > -1 ? data[i]['이메일'] : '#' + data[i]['이메일'] + '#';
								result = false;
							}
						}
						resolve({ data: data, result: result });
					};
					reader.readAsBinaryString(file);
					break;
			}
		});
	} else {
		showNotification('유효성 검사', '파일이 존재하지 않습니다.', 'danger');
		return false;
	}
}

export async function excelDownload(jsonDataList, key, options = {}, t = i18next.t) {
	const xlsx = require('sheetjs-style-v2');
	let workSheetDatas = [];
	let sheetName;
	const { positions } = options;
 
	switch (key) {
		case 'userList':
			sheetName = t('adminMode.popup.employeeInformation') || '사원정보';
			jsonDataList.map((x) => {
				let attnedType = [];
				x?.group?.area?.gps && attnedType.push('GPS');
				x?.group?.area?.wifi?.active && attnedType.push('WIFI');
				!isEmpty(x?.secomNumber) && attnedType.push('출입연동');

				let deviceModel = [];
				deviceModel = x.device.map((y) => y.os);
				deviceModel = new Set(deviceModel);
				deviceModel = [...deviceModel];

				let state = '';
				switch (x.state) {
					case 'SUCCESS':
						state = t('재직');
						break;
					case 'LEAVE':
						state = t('퇴직');
						break;
					case 'REST':
						state = t('휴직');
						break;
					case 'SUSPENSION':
						state = t('정직(징계)');
						break;
					case 'REQUEST':
						state = t('restManage.requestMembership');
						break;
					case 'DENY':
						state = t('restManage.rejectRequest');
						break;
				}

				const depart = x.departmentsList?.find((x) => x.main) || {};
				const position = positions?.filter((p) => p.id * 1 === depart?.positionId * 1);
				const jobResponsibilityNm = t(position?.length ? position[0].label : '');
		    	const tr = (name) => t('memberList.' + name);
				let workSheetData = {
					[tr('name')]: getPersonDisplayName(x.user) || '',
					[tr('employeeNumber')]: x.companyNumber || '',
					[tr('email')]: x.user?.email || '',
					[tr('department')]: localizedLabel(t, depart?.department),
					[tr('affiliatedWorkGroup')] : localizedLabel(t, x.group) || '',
					[tr('jobResponsibility')] : localizedLabel(t, x.rank) || '',
					[tr('position')] : jobResponsibilityNm,
					[tr('authenticationMethod')] : attnedType?.join('/') || '',
					[t('고용유형')] : getLocalizedField(x, 'hireTypeNm') || '',
					[tr('authority')] : t(x.role?.name) || '',
					[tr('device')] : deviceModel?.join('/') || '',
					[tr('workStatus')] : state,
				};
				workSheetDatas.push(workSheetData);
			});
			break;
			case 'deadlineAttendList':
				sheetName = t('근무기록');
				jsonDataList.map((x) => {
					let workSheetData = {
						[t('사업장')]: getLocalizedField(x, 'org_name') || '',
						[t('근무일자')]: x.work_date || '',
						[t('사원번호')]: x.company_number || '',
						[t('성명')]: x.name || '',
						[t('직책')]: x.rank_name || '',
						[t('직위')]: x.position_name || '',
						[t('부서')]: x.dpt_name ||'',
						[t('근무상태')]: x.state ||'',
						[t('근무그룹')]: getLocalizedField(x, 'wgp_name') || '',
						[t('근무그룹출근시간')]: getDateTimeField(x.wgp_work_stime), // x.wgp_work_stime ? new Date(x.wgp_work_stime).toLocaleString("ko-KR", { timeZone: "Asia/Seoul" }) : '',
						[t('근무그룹퇴근시간')]: getDateTimeField(x.wgp_work_etime), // x.wgp_work_etime ? new Date(x.wgp_work_etime).toLocaleString("ko-KR", { timeZone: "Asia/Seoul" }) : '',
						[t('워크시스템명')]: x.ws_name || '',
						[t('연장기준')]: x.is_success_work_over ? t(x.is_success_work_over) : '',
						[t('근무스케쥴명')]: x.ws_title || '',
						[t('근무스케쥴시작일시')]: getDateTimeField(x.ws_start_time), // x.ws_start_time ? new Date(x.ws_start_time).toLocaleString("ko-KR", { timeZone: "Asia/Seoul" }) : '',
						[t('근무스케쥴종료일시')]: getDateTimeField(x.ws_end_time), // x.ws_end_time ? new Date(x.ws_end_time).toLocaleString("ko-KR", { timeZone: "Asia/Seoul" }) : '',
						[t('근무스케쥴휴게시간수동여부')]: x.scheduleRestAuto || '',
						[t('근무스케쥴휴게시간')]: x.scheduleRestTime || '',
						[t('주간근무시간')]: x.working_time || '',
						[t('야간근무시간')]: x.night_time || '',
						[t('연장근무시간')]: x.working_over_time || '',
						[t('야간연장근무시간')]: x.night_over_time || '',
						[t('휴게시간')]: x.rest_time || '',
						[t('연장휴게시간')]: x.rest_over_time || '',
						[t('휴일근무여부')]: x.weekly || '',
						[t('마감순번')]: x.hist_seq || '',
						[t('관리자확정일자')]: x.cnfrm_time || '',
						[t('관리자확정처리작업자')]: x.cnfrm_worker || '',
						[t('공사현장')]: x.constr_site || '',
						[t('공사직or간접원')]: x.constr_rate || '',
						[t('공사유형')]: x.constr_type || '',
						[t('공사년차')]: x.constr_year || '',
						[t('공사급수')]: x.constr_grade || '',
						[t('현장소장여부')]: x.constr_mgr || '',
						[t('공사직급')]: x.constr_rank || '',
					};
					workSheetDatas.push(workSheetData);
				});
				break;
			case 'deadlineOverList':
				sheetName = t('52시간 초과자');
				jsonDataList.map((x) => {
					let workSheetData = {
						[t('사업장')]: getLocalizedField(x, 'org_name') || '',
						[t('사원번호')]: x.company_number || '',
						[t('성명')]: x.user_name || '',
						[t('직위')]: x.rank_name || '',
						[t('직책')]: x.position_name || '',
						[t('부서')]: x.dpt_name ||'',
						[t('근무상태')]: localizedLabel(t, x, 'state_name') || '',
						[t('근무그룹')]: getLocalizedField(x, 'wgp_name') || '',
						[t('주차')]: x.wk_num || '',
						[t('주간총근로시간')]: x.tot_wk_time || '',
						[t('주52시간초과여부')]: x.over_52_yn || '',
						[t('주52시간초과시간')]: x.over_52_seconds || '',
						[t('주연장12시간초과여부')]: x.over_12_yn || '',
						[t('연장12시간초과근로시간')]: x.over_12_seconds || '',
					};
					workSheetDatas.push(workSheetData);
				});
				break;
			case 'workScheduleList':
				sheetName = t('근태스케줄'); 
				jsonDataList.map((x) => {
					// console.log('getDateTimeField(x.start_time || x.startTime) : ' + x.start_time || x.startTime);
					let workSheetData = {
						[`${t('사업장')}[${t('선택')}]`]: getLocalizedField(x, 'org_name') || getLocalizedField(x, 'orgName') || '',
						[`${t('이름')}[${t('선택')}]`]: getPersonDisplayName(x) || '',
						[`${t('사번')}[${t('필수')}]`]: x.company_number || x.companyNumber || '',
						[`${t('직위')}[${t('선택')}]`]: x.rank_name || getLocalizedField(x, 'rankName') || '',
						[`${t('직책')}[${t('선택')}]`]: x.position_name || getLocalizedField(x, 'positionName') || '',
						[`${t('부서')}[${t('선택')}]`]: x.dpt_name || getLocalizedField(x, 'dptName') ||'',
						[`${t('근무상태')}[${t('선택')}]`]: localizedLabel(t, x, x.state_name ? 'state_name' : 'stateName') || '',
						[`${t('근무그룹')}[${t('선택')}]`]: x.group_name || getLocalizedField(x, 'groupName') || '',
						[`${t('근무일자')}[${t('필수')}]`]: x.work_date || x.workDate || '',
						[`${t('시작시간')}[${t('선택')}]`]: moment(x.start_time || x.startTime).format('HH:mm'), // x.start_time ? new Date(x.start_time).toLocaleString("ko-KR", { timeZone: "Asia/Seoul" }) : '',
						[`${t('종료시간')}[${t('선택')}]`]: moment(x.end_time || x.endTime).format('HH:mm'), // x.end_time ? new Date(x.end_time).toLocaleString("ko-KR", { timeZone: "Asia/Seoul" }) : '',
						// 템플릿 번역명이 없으면 t(원본) — 화면 고정 템플릿(주휴차감)도 번역된다
						[`${t('템플릿 이름')}[${t('선택')}]`]: x.schedule_name || localizedLabel(t, x, 'scheduleName') || '',
						[`${t('템플릿 코드')}[${t('필수')}]`]: x.schedule_code || x.scheduleCode || '',
						[`${t('주간근무시간')}[${t('선택')}]`]: x.working_time || x.workingTime || '',
						[`${t('야간근무시간')}[${t('선택')}]`]: x.night_time || x.nightTime || '',
						[`${t('연장근무시간')}[${t('선택')}]`]: x.working_over_time || x.workingOverTime || '',
						[`${t('야간연장근무시간')}[${t('선택')}]`]: x.night_over_time || x.nightOverTime || '',
						[`${t('휴게시간')}[${t('선택')}]`]: x.rest_time || x.restTime || '',
						[`${t('연장휴게시간')}[${t('선택')}]`]: x.rest_over_time || x.restOverTime || '',
						[`${t('휴일근무여부')}[${t('선택')}]`]: x.weekly || ''
					};
					workSheetDatas.push(workSheetData);
				});
				break;
		default:
			break;
	}

	let wb = xlsx.utils.book_new();
	let ws = xlsx.utils.json_to_sheet(workSheetDatas);

	const range = xlsx.utils.decode_range(ws['!ref'])
	const borderStyle = {
		top: {
			style: 'thin',
			color: { rgb: 'FFD9DDEB' },
		},
		bottom: {
			style: 'thin',
			color: { rgb: 'FFD9DDEB' },
		},
		left: {
			style: 'thin',
			color: { rgb: 'FFD9DDEB' },
		},
		right: {
			style: 'thin',
			color: { rgb: 'FFD9DDEB' },
		},
	};

	const bodyStyle = {
		font: {
			name: '맑은 고딕',
			bold: false,
			sz: 10,
			color: { rgb: 'FF333333' },
		},
		alignment: {
			horizontal: 'center',
			vertical: 'center',
			wrapText: true,
		},
		border: borderStyle,
	};

	const zebraFillStyle = {
		patternType: 'solid',
		fgColor: { rgb: 'FFF8F9FC' },
	};

	let columsW = []
	let wsrows = []
	for (let R = range.s.r; R <= range.e.r; ++R) {
		if (R === 0)
			wsrows.push({ hpt: key === 'workScheduleList' ? 45 : 32 })
		else
			wsrows.push({ hpt: 18 })
		for (let C = range.s.c; C <= range.e.c; ++C) {
			const cell_address = { c: C, r: R };
			const cell_ref = xlsx.utils.encode_cell(cell_address);
			let cell = ws[cell_ref]

			if (!columsW[C])
				columsW[C] = 13

			switch (typeof (cell.v)) {
				case 'string':
					if (columsW[C] < cell.v.length * 1.5)
						columsW[C] = cell.v.length * 1.5
					break
				case 'number':
					if (columsW[C] < cell.v.length)
						columsW[C] = cell.v.length
					break
			}

			if (R === 0) {
				const cellValue = cell?.v?.toString()?.trim() || '';

				// [선택] 또는 [필수] 포함 시 줄바꿈 적용
				if (cellValue.includes(`[${t('선택')}]`) || cellValue.includes(`[${t('필수')}]`)) {
					// '[' 앞에 줄바꿈 추가
					const replaced = cellValue.replace('[', '\n[');
					ws[cell_ref].v = replaced;
					ws[cell_ref].t = 's';
				}

				ws[cell_ref].s = {
					font: {
						name: '맑은 고딕',
						bold: true,
						sz: 11,
						color: { rgb: "FFFFFFFF" }
					},
					fill: {
						patternType: "solid",
						// [필수] 포함 시 빨간색으로 표기
						bgColor: { rgb: cellValue.includes(t('필수')) ? "FFFF5555" : "FF556EE6" },
						fgColor: { rgb: cellValue.includes(t('필수')) ? "FFFF5555" : "FF556EE6" }
					},
					alignment: {
						vertical: "center",
						horizontal: "center",
						wrapText: true
					},
					border: borderStyle
				}
			} else {
				ws[cell_ref].s = {
					...bodyStyle,
					...(R % 2 === 0 && {
						fill: zebraFillStyle,
					}),
				}
			}
		}
	}

	let wscols = []
	columsW.map((v, i) => {
		wscols.push({ wch: parseInt(v) * 1.2 })
	})
	ws['!cols'] = wscols;
	ws['!rows'] = wsrows;
	// 자동 필터
	ws['!autofilter'] = {
		ref: ws['!ref'],
	};

	xlsx.utils.book_append_sheet(wb, ws, sheetName);
	xlsx.writeFile(wb, `${moment().format('YYYY-MM-DD')}_${sheetName}.xlsx`);
}

// 글자수 자르기
export function truncateString(str, maxLength) {
	if (str.length > maxLength) {
		return str.slice(0, maxLength) + '...';
	} else {
		return str;
	}
}

export const getLineUser = (approvalList = []) => {
	const userList = [];
	approvalList?.forEach((approval) => {
		if(Array.isArray(approval.line)){ // 방어코드 추가
			approval.line.forEach((user) => {
				userList.push(user.user);
			});
		}
	});
	return userList;
};

export const convertMinsToHrsMins = (seconds) => {
	if(seconds <= 0) return '-';
	let h = Math.floor(seconds / 3600);
	let m = Math.floor((seconds % 3600) / 60);

	h = h < 10 ? '0' + h : h;
	m = m < 10 ? '0' + m : m;
	return `${h}:${m}`;
};

export const convertMinsToHrsMins_v2 = (seconds) => {
	if(seconds <= 0) return t('{{h}}시간', { h: 0 });

	let h = Math.floor(seconds / 3600);
	let m = Math.floor((seconds % 3600) / 60);
	m = m < 10 ? '0' + m : m;
	return `${h > 0 ? t('{{h}}시간', { h }) : ''}${m > 0 ? ` ${t('{{m}}분', { m })}` : ''}`;
};

export const getCutOffLabel = (cutOffTime, deduction, t) => {
	const am = t ? t('오전') : '오전';
	const pm = t ? t('오후') : '오후';
	switch (cutOffTime) {
		case 1: return `(${am})`;
		case 2: return `(${pm})`;
		case 3: return `(${convertMinsToHrsMins_v2(deduction * 3600)})`;
		default: return '';
	}
};

export function excelSerialDateToJSDate(excelSerialDate) {
	const daysBeforeUnixEpoch = 70 * 365 + 19;
	const hour = 60 * 60 * 1000;
	return new Date(Math.round((excelSerialDate - daysBeforeUnixEpoch) * 24 * hour) + 12 * hour);
}

export function excelSerialTimeToJSTime(excelSerialDate) {
	// 0.333333333 > 08:00
	return convertMinsToHrsMins(Math.round(excelSerialDate * (24 * 60 * 60)));
}

export function generateState() {
    const stateBytes = new Uint8Array(32);
    crypto.getRandomValues(stateBytes);
    return btoa(String.fromCharCode(...stateBytes)).replace(/=/g, '').replace(/\+/g, '-').replace(/\//g, '_');
}
export function generateNonce() {
    const nonceBytes = new Uint8Array(32);
    crypto.getRandomValues(nonceBytes);
    return btoa(String.fromCharCode(...nonceBytes)).replace(/=/g, '').replace(/\+/g, '-').replace(/\//g, '_');
}

function degreesToRadians(degrees) {
	const radians = (degrees * Math.PI)/180;
	return radians;
  }
  
  export function computeDistance(startCoords, destCoords) {
  
	// console.log(startCoords, destCoords)
  
	const startLatRads = degreesToRadians(startCoords.latitude);
	const startLongRads = degreesToRadians(startCoords.longitude);
	const destLatRads = degreesToRadians(destCoords.latitude);
	const destLongRads = degreesToRadians(destCoords.longitude);
  
	const Radius = 6371; //지구의 반경(km)
	const distance = Math.acos(Math.sin(startLatRads) * Math.sin(destLatRads) + 
					Math.cos(startLatRads) * Math.cos(destLatRads) *
					Math.cos(startLongRads - destLongRads)) * Radius;
  
	return distance;
  }

export function getDistanceBetweenCoords(lat1, lng1, lat2, lng2, returnUnit = 'km') {
    if (!window.kakao || !window.kakao.maps) {
      console.warn('Kakao Maps SDK is not loaded.');
      return 0;
    }

    // Create two LatLng points
    const linePath = [
      new kakao.maps.LatLng(lat1, lng1),
      new kakao.maps.LatLng(lat2, lng2)
    ];

    // Create a Polyline with those two points
    const polyline = new kakao.maps.Polyline({
      path: linePath
    });

    // Get the length in meters
    const distanceInMeters = polyline.getLength();

    if (isNaN(distanceInMeters)) {
      return '';
    }

    // Return result in requested unit
    return returnUnit === 'km'
      ? +(distanceInMeters / 1000).toFixed(2) // Convert to kilometers and round to 2 decimals
      : Math.round(distanceInMeters);         // Round to nearest meter
}

export const isLocationInKorean = (latitude, longitude, callback) => {
	if (!window.kakao?.maps) {
		callback && callback(false);
		return;
	}
	window.kakao.maps.load(() => {
		try {
			const geocoder = new window.kakao.maps.services.Geocoder();
			geocoder.coord2RegionCode(longitude, latitude, (result, status) => {
				if (status === window.kakao.maps.services.Status.OK) {
					const address = result[0]?.address_name;
					callback && callback(!!address);
				} else {
					callback && callback(false);
				}
			});
		} catch (error) {
			callback && callback(false);
			console.error("Error checking location in Korea:", error);
		}
	});
}

export const debugLog = (...args) => {
    const isDebug = localStorage.getItem('debug') === 'true';

    if (isDebug) {
        console.log('%c[DEBUG]', 'color: #00bcd4; font-weight: bold;', ...args);
    }
};

export const getDeptNameFromTree = (userId, nodes, parentTitle = '') => {
	if (!userId || !nodes?.length) return null;
	for (const node of nodes) {
		if (node.userId && parseInt(node.userId) === parseInt(userId)) return parentTitle;
		if (node.children?.length) {
			const found = getDeptNameFromTree(userId, node.children, getLocalizedField(node) || node.title || parentTitle); // 표시용: 부서 번역명 우선
			if (found !== null) return found;
		}
	}
	return null;
};
